/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ID } from '@gh/common-shared-data';

import { AbstractRestService, arrayOf, Enum, identity, none, optimisticLockingOf, RestSettings } from '@gh/core-data';
import { parametrizeUrl } from '@gh/core-util';

import { Observable } from 'rxjs';

import { CheckUsageDto, CheckUsageMapper } from '../common';
import {
  ScheduleTemplateDetailsDto,
  ScheduleTemplateListFilter,
  ScheduleTemplateListItemDto,
  ScheduleTemplateTaskDetailsDto,
  ScheduleType,
} from './schedule-template.dto';
import {
  ScheduleTemplateDetailsMapper,
  ScheduleTemplateFilterMapper,
  ScheduleTemplateListItemMapper,
  ScheduleTemplateTaskDetailsMapper,
} from './schedule-template.mapper';

@Injectable()
export class ScheduleTemplateRestService extends AbstractRestService {
  constructor(private settings: RestSettings) {
    super(settings);
  }

  findAll(filter: ScheduleTemplateListFilter): Observable<ScheduleTemplateListItemDto[]> {
    return this.httpGet(
      `${this.bp}/schedule-templates`, filter, ScheduleTemplateFilterMapper, arrayOf(ScheduleTemplateListItemMapper));
  }

  findAllForAutocomplete(type: ScheduleType): Observable<ScheduleTemplateListItemDto[]> {
    return this.httpGet(
      `${this.bp}/schedule-templates/autocomplete`,
      { type: Enum.toValue(ScheduleType, type) },
      identity(),
      arrayOf(ScheduleTemplateListItemMapper));
  }

  findAllByOffice(officeId: ID, filter?: ScheduleTemplateListFilter): Observable<ScheduleTemplateListItemDto[]> {
    return this.httpPost(
      `${this.bp}/schedule-templates/finder/${officeId}`,
      filter,
      ScheduleTemplateFilterMapper,
      arrayOf(ScheduleTemplateListItemMapper));
  }

  init(filter: ScheduleTemplateListFilter): Observable<ScheduleTemplateDetailsDto> {
    return this.httpGet(
      `${this.bp}/schedule-templates/init`, filter, ScheduleTemplateFilterMapper, ScheduleTemplateDetailsMapper);
  }

  get(templateId: ID): Observable<ScheduleTemplateDetailsDto> {
    return this.httpGet(`${this.bp}/schedule-templates/${templateId}`, ScheduleTemplateDetailsMapper);
  }

  create(scheduleTemplate: ScheduleTemplateDetailsDto): Observable<ScheduleTemplateDetailsDto> {
    return this.httpPost(`${this.bp}/schedule-templates`,
                         scheduleTemplate.type === ScheduleType.Construction ?
                           this.scheduleDtoToRequest(scheduleTemplate) :
                           scheduleTemplate,
                         ScheduleTemplateDetailsMapper);
  }

  update(scheduleTemplate: ScheduleTemplateDetailsDto): Observable<ScheduleTemplateDetailsDto> {
    return this.httpPut(
      `${this.bp}/schedule-templates/${scheduleTemplate.templateId}`,
      scheduleTemplate.type === ScheduleType.Construction ?
        this.scheduleDtoToRequest(scheduleTemplate) :
        scheduleTemplate,
      ScheduleTemplateDetailsMapper);
  }

  recalculate(scheduleTemplate: ScheduleTemplateDetailsDto): Observable<ScheduleTemplateDetailsDto> {
    return this.httpPut(
      `${this.bp}/schedule-templates/recalculate`, scheduleTemplate, ScheduleTemplateDetailsMapper);
  }

  checkUsage({ id, version }: ScheduleTemplateListItemDto): Observable<CheckUsageDto> {
    return this.httpGet(
      `${this.bp}/schedule-templates/check-usage/${id}`, { version }, void 0, CheckUsageMapper,
    );
  }

  deactivateOrDelete({ id }: ScheduleTemplateListItemDto, checkUsage: CheckUsageDto): Observable<CheckUsageDto> {
    return this.httpDelete(
      `${this.bp}/schedule-templates/${id}`, checkUsage, CheckUsageMapper);
  }

  checkDelete({ templateId }: ScheduleTemplateDetailsDto, templateTaskId: ID): Observable<void> {
    return this.httpGet(
      `${this.bp}/schedule-templates/${templateId}/validate-task?taskId=${templateTaskId}`, none());
  }

  activate({ id, version }: ScheduleTemplateListItemDto): Observable<ScheduleTemplateListItemDto> {
    return this.httpPut(
      `${this.bp}/schedule-templates/undelete/${id}?version=${version}`,
      void 0,
      optimisticLockingOf(ScheduleTemplateListItemMapper));
  }

  getTask(templateId: ID, taskId: ID): Observable<ScheduleTemplateTaskDetailsDto> {
    return this.httpGet(
      `${this.bp}/schedule-templates/${templateId}/tasks/${taskId}`, ScheduleTemplateTaskDetailsMapper);
  }

  updateTask(templateId: ID,
             taskId: ID,
             scheduleTemplateTask: ScheduleTemplateTaskDetailsDto): Observable<ScheduleTemplateTaskDetailsDto> {
    return this.httpPut(
      `${this.bp}/schedule-templates/${templateId}/tasks/${taskId}`, scheduleTemplateTask, ScheduleTemplateTaskDetailsMapper);
  }

  setDefaultForWorksheet({ id, version }: ScheduleTemplateListItemDto): Observable<void> {
    return this.httpPut(parametrizeUrl(`${this.bp}/schedule-templates/${id}/worksheet`, { version }), void 0, identity(), none());
  }

  private scheduleDtoToRequest(schedule: ScheduleTemplateDetailsDto): ScheduleTemplateDetailsDto {
    return {
      ...schedule,
      tasks: schedule.tasks.map((task) => ({
        ...task,
        isInSupervisorSchedule: true,
      })),
    };
  }
}
